import { Skeleton } from '@/shared/components/ui/skeleton'
import { Typography } from '@/shared/components/ui/typography'
import { cn } from '@/shared/lib/utils'
import { IRecommendationItem } from '@/types/recommendations'
import React from 'react'

export type LearningRecommendationCardProps =
  React.ComponentProps<'article'> & {
    item?: IRecommendationItem
    isLoading?: boolean
    prefix?: React.ReactNode
  }

export function LearningRecommendationCard({
  item,
  isLoading,
  prefix,
  ...props
}: LearningRecommendationCardProps) {
  const firstInstructor = item?.instructors?.[0]

  const cardContent = (
    <article
      {...props}
      className={cn(
        'LearningRecommendationCard rounded-lg border text-mydra-black transition-colors',
        !isLoading && 'hover:bg-secondary-lighter/30',
        props.className
      )}
    >
      <div className="flex flex-col gap-6 p-3">
        <header className="flex flex-col gap-4">
          <Typography variant="h3" className="inline-flex gap-2 font-gt-planar">
            {prefix && (
              <span
                className={cn(
                  'font-bold md:font-normal',
                  isLoading
                    ? 'animate-pulse text-grey-03'
                    : 'text-grey-03 md:text-primary-light'
                )}
              >
                {prefix}
              </span>
            )}
            {isLoading ? (
              <Skeleton className="h-7 w-full max-w-[300px]" />
            ) : (
              <span className="line-clamp-1 text-ellipsis max-md:text-lg">
                {item?.title}
              </span>
            )}
          </Typography>
        </header>

        <div className="mt-auto flex items-center">
          <div className="flex items-center gap-2">
            <div className="flex shrink-0">
              {isLoading ? (
                <>
                  <Skeleton className="inline-block h-10 w-10 shrink-0 rounded-full" />
                  <Skeleton className="-ml-2 inline-block h-10 w-10 shrink-0 rounded-full" />
                </>
              ) : (
                <>
                  {firstInstructor?.thumbnailUrl && (
                    <img
                      className="inline-block h-10 w-10 shrink-0 rounded-full object-cover"
                      src={firstInstructor.thumbnailUrl}
                      alt={firstInstructor.name}
                    />
                  )}
                  {item?.provider.thumbnailUrl && (
                    <img
                      className={cn(
                        'inline-block h-10 w-10 shrink-0 rounded-full object-cover',
                        firstInstructor?.thumbnailUrl && '-ml-2'
                      )}
                      src={item.provider.thumbnailUrl}
                      alt={item.provider.name}
                    />
                  )}
                </>
              )}
            </div>

            <div className="flex flex-col justify-center">
              {isLoading ? (
                <Skeleton className="h-5 w-24" />
              ) : (
                <Typography
                  variant="body2"
                  className="font-semibold text-mydra-gray md:text-base"
                >
                  {item?.provider.name}
                </Typography>
              )}
            </div>
          </div>
          <div
            className={cn(
              'ml-auto self-center rounded-sm px-2 py-1',
              isLoading ? '' : 'border'
            )}
          >
            {isLoading ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              <Typography
                variant="body2"
                className="font-semibold text-mydra-gray md:text-base"
              >
                {item?.type}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </article>
  )

  if (isLoading || !item) {
    return cardContent
  }

  return (
    <a href={item.url} target="_blank" rel="noopener noreferrer">
      {cardContent}
    </a>
  )
}
